import { NSARouterMakerResult } from "namirasoft-account-react";
import { IStorageLocal } from "namirasoft-core";
import { ChatRow, EntityType, RunHelper, RunRow } from "namirasoft-expert";
import { NamirasoftMap } from "namirasoft-site-map";
import { JsonFormatter, NSButtonBlue } from "namirasoft-site-react";
import { Component, ReactNode } from "react";
import { ExpertRouterProps } from "../ExpertRouterProps";
import { AgentTestPageInner } from "../pages/AgentTestPage";
import RunLayout from "./RunLayout";
import Styles from "./RunTest.module.css";
import Task, { TaskItem, TaskRequested, TaskResult } from "./Task";

interface TestTrackOutput
{
    input?: { chat?: ChatRow };
    tasks_initialized: boolean;
    tasks: TaskItem[];
    task_requested: Record<string, TaskRequested>;
    task_result: Record<string, TaskResult>;
    error: { node: string; message: string } | null;
}

interface RunTestProps extends NSARouterMakerResult, ExpertRouterProps
{
    run: RunRow;
    agent_id: string;
    onRunChanged?: (run: RunRow) => void;
    onChatChanged?: (chat: ChatRow) => void;
}

interface RunTestState
{
    run: RunRow;
    track: TestTrackOutput | null;
}

class RunTest extends Component<RunTestProps, RunTestState>
{
    private fetching = false;
    private unmounted = false;
    private trackHash: string | null = null;
    private chatHash: string | null = null;
    private pollTimer: ReturnType<typeof setInterval> | null = null;
    private jsonFormatter = new JsonFormatter("100%");
    private namirasoftMap: NamirasoftMap;
    private storage = new IStorageLocal();

    constructor(props: RunTestProps)
    {
        super(props);
        this.state = { run: props.run, track: null };
        this.namirasoftMap = new NamirasoftMap(props.account.token_manager, console.error);
    }

    override componentDidMount(): void
    {
        this.reload();
        this.pollTimer = setInterval(() =>
        {
            if (this.unmounted || RunHelper.isTerminalStatus(this.state.run.status))
            {
                clearInterval(this.pollTimer!);
                this.pollTimer = null;
                return;
            }
            this.reload();
        }, 1000);
    }

    override componentWillUnmount(): void
    {
        this.unmounted = true;
        if (this.pollTimer)
        {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private async reload(): Promise<void>
    {
        if (this.fetching)
            return;

        this.fetching = true;
        try
        {
            let response = await this.props.servers.expert.test.Track(this.state.run.id);
            if (!this.unmounted && response)
            {
                let chat = response.input?.chat;
                if (chat)
                {
                    let newChatHash = JSON.stringify(chat);
                    if (newChatHash !== this.chatHash)
                    {
                        this.chatHash = newChatHash;
                        this.props.onChatChanged?.(chat);
                    }
                }
                let newHash = JSON.stringify(response);
                if (newHash !== this.trackHash)
                {
                    this.trackHash = newHash;
                    this.setState({ track: response as unknown as TestTrackOutput });
                }
            }
        }
        catch { }
        finally
        {
            this.fetching = false;
        }
    }

    private async rerun(onFinished: () => void): Promise<void>
    {
        const run = this.state.run;
        const meta = run.meta as { entity_type?: string; entity_id?: string } | null;
        debugger;
        try
        {
            if (!meta?.entity_type || !meta?.entity_id)
                return;
            AgentTestPageInner.RunTest(this.props, this.props.agent_id, meta.entity_type as EntityType, meta.entity_id);
        }
        catch (error)
        {
            this.props.notifier.onError(error as Error);
        }
        finally
        {
            onFinished();
        }
    }

    private renderError(error: { node: string; message: string } | null): ReactNode
    {
        if (!error)
            return null;
        return (
            <div className={Styles.ns_run_test_error_box}>
                <span className={Styles.ns_run_test_error_node}>[{error.node}]</span>
                {error.message}
            </div>
        );
    }

    override render()
    {
        let track = this.state.track;
        return (
            <RunLayout
                {...this.props}
                run={this.state.run}
                onRunChanged={run =>
                {
                    this.setState({ run }, () => this.reload());
                    this.props.onRunChanged?.(run);
                }}
            >
                {!track && (
                    <div className={Styles.ns_run_test_loading}>Loading test track…</div>
                )}
                {track && (
                    <div className={Styles.ns_run_test}>
                        {!track.tasks_initialized && track.tasks.length === 0 && (
                            <div className={Styles.ns_run_test_loading}>Initializing tasks…</div>
                        )}
                        {(track.tasks_initialized || track.tasks.length > 0) && (
                            <Task
                                sectionKey="test_tasks"
                                title="Tasks"
                                tasks={track.tasks}
                                taskRequested={track.task_requested}
                                taskResult={track.task_result}
                                namirasoftMap={this.namirasoftMap}
                                storage={this.storage}
                                jsonFormatter={this.jsonFormatter}
                                initiallyOpen={true}
                            />
                        )}
                        {this.renderError(track.error)}
                    </div>
                )}
                {
                    RunHelper.isTerminalStatus(this.state.run.status) && (
                        <div className={Styles.ns_run_test_rerun}>
                            <NSButtonBlue
                                title="Rerun"
                                onClick={{
                                    showLoading: true,
                                    action: (onFinished) => this.rerun(onFinished)
                                }}
                            />
                        </div>
                    )}
            </RunLayout>
        );
    }
}

export default RunTest;